import React, { useState } from 'react';
import { GSOCDateRangePicker } from '../components/inputs/GSOCDateRangePicker';
import { CommonAppLayout } from '../components/CommonAppLayout';
import { ContentLayout, FormField, Header, Input, Spinner } from '@amzn/awsui-components-console';
import { RequiresPermissionClaims } from '../components/RequiresPermissionClaims';
import { PermissionClaim } from '../code-generated/PermissionClaim.generated';
import { BREADCRUMBS } from './pages';
import type {
  DdxMetricsResultFragment,
  LfMetricsResultFragment,
  ListDdxMetricsQuery,
} from '../code-generated/graphqlOperations.generated';
import {
  SortOrder,
  useListDdxMetricsQuery,
  useListLfMetricsQuery,
} from '../code-generated/graphqlOperations.generated';
import { uniqBy } from 'lodash';
import { format } from 'date-fns';
import { DDXGraphs } from '../components/dataplane-dashboard/DDXMetricsComponent';
import { useSearchParamDateRange } from '../hooks/useSearchParamDateRange';
import { LaminarFlowGraphs } from '../components/dataplane-dashboard/LaminarFlowMetricsComponent';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

const DEFAULT_QUERY_LIMIT = 1000;
const FORMATTED_TIME_STR = 'yyyy-MM-dd HH:mm:ss.SSS';

export function DataPlaneDashboardPage() {
  const [dateRange, setDateRange] = useSearchParamDateRange();
  const [startRegion, setStartRegion] = useState<string>('');
  const [endRegion, setEndRegion] = useState<string>('');
  const [customerNamePart, setCustomerNamePart] = useState<string>('');

  const debouncedStartRegion = useDebouncedValue<string>(startRegion, 500);
  const debouncedEndRegion = useDebouncedValue<string>(endRegion, 500);
  const debouncedCustomerNamePart = useDebouncedValue<string>(customerNamePart, 500);

  // TODO - Combine these queries and 'fetchMoreXXX' useEffect's into their own hook at some point once we have all our data present.
  const {
    data: ddxData,
    fetchMore: fetchMoreDDXMetrics,
    loading: ddxLoading,
  } = useListDdxMetricsQuery({
    variables: {
      where: {
        _and: [
          {
            endTime: { _lte: format(dateRange.endDate, FORMATTED_TIME_STR) },
          },
          {
            startTime: { _gte: format(dateRange.startDate, FORMATTED_TIME_STR) },
          },
          ...(debouncedCustomerNamePart !== ''
            ? [
                {
                  customerNamePart: { _eq: debouncedCustomerNamePart },
                },
              ]
            : [{}]),
          ...(debouncedStartRegion !== ''
            ? [
                {
                  region: { _eq: debouncedStartRegion },
                },
              ]
            : [{}]),
        ],
      },
      limit: DEFAULT_QUERY_LIMIT,
      sort: [
        {
          startTime: SortOrder.Desc,
        },
      ],
    },
  });

  React.useEffect(() => {
    if (ddxData?.listDataPlaneDDXMetric?.pageInfo.hasNextPage) {
      void fetchMoreDDXMetrics({
        variables: {
          cursor: ddxData.listDataPlaneDDXMetric.pageInfo.lastCursor?.map(cursor => {
            return {
              // We do this map because the GraphQL query errors when the __typename is included.
              key: cursor.key,
              value: cursor.value,
            };
          }),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const mergedResults = uniqBy(
            [
              ...(prev.listDataPlaneDDXMetric?.results ?? []),
              ...(fetchMoreResult.listDataPlaneDDXMetric?.results ?? []),
            ],
            metric => metric.jobId,
          );

          return {
            ...fetchMoreResult,
            listDataPlaneDDXMetric: {
              ...fetchMoreResult.listDataPlaneDDXMetric,
              results: mergedResults,
            },
          } as ListDdxMetricsQuery;
        },
      });
    }
  }, [
    ddxLoading,
    ddxData?.listDataPlaneDDXMetric?.pageInfo.hasNextPage,
    ddxData?.listDataPlaneDDXMetric?.pageInfo.lastCursor,
    ddxData?.listDataPlaneDDXMetric?.pageInfo,
    fetchMoreDDXMetrics,
  ]);

  const {
    data: lfData,
    fetchMore: fetchMoreLfMetrics,
    loading: lfLoading,
  } = useListLfMetricsQuery({
    variables: {
      where: {
        _and: [
          {
            timeStampEnd: { _lte: format(dateRange.endDate, FORMATTED_TIME_STR) },
          },
          {
            timeStampStart: { _gte: format(dateRange.startDate, FORMATTED_TIME_STR) },
          },
          {
            lfEgressStageMetricsV2: { _exists: true },
          },
          ...(debouncedCustomerNamePart !== ''
            ? [
                {
                  customerNamePart: { _eq: debouncedCustomerNamePart },
                },
              ]
            : [{}]),
          ...(debouncedStartRegion !== ''
            ? [
                {
                  sourceRegion: { _eq: debouncedStartRegion },
                },
              ]
            : [{}]),
          ...(debouncedEndRegion !== ''
            ? [
                {
                  destinationRegion: { _eq: debouncedEndRegion },
                },
              ]
            : [{}]),
        ],
      },
      limit: DEFAULT_QUERY_LIMIT,
      sort: [
        {
          timeStampStart: SortOrder.Desc,
        },
      ],
    },
  });

  React.useEffect(() => {
    if (lfData?.listDataPlaneLFMetric?.pageInfo.hasNextPage) {
      void fetchMoreLfMetrics({
        variables: {
          cursor: lfData.listDataPlaneLFMetric.pageInfo.lastCursor?.map(cursor => {
            return {
              // We do this map because the GraphQL query errors when the __typename is included.
              key: cursor.key,
              value: cursor.value,
            };
          }),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const mergedResults = uniqBy(
            [...(prev.listDataPlaneLFMetric?.results ?? []), ...(fetchMoreResult.listDataPlaneLFMetric?.results ?? [])],
            metric => metric.id,
          );

          return {
            ...fetchMoreResult,
            listDataPlaneLFMetric: {
              ...fetchMoreResult.listDataPlaneLFMetric,
              results: mergedResults,
            },
          } as ListDdxMetricsQuery;
        },
      });
    }
  }, [
    lfLoading,
    fetchMoreLfMetrics,
    lfData?.listDataPlaneLFMetric?.pageInfo.hasNextPage,
    lfData?.listDataPlaneLFMetric?.pageInfo.lastCursor,
  ]);

  return (
    <CommonAppLayout
      content={
        <ContentLayout header={<Header variant='h1'>Data Plane Dashboard</Header>}>
          <RequiresPermissionClaims claims={[PermissionClaim.ReadMaintenance]}>
            <FormField
              label='Date Range Selection (UTC)'
              constraintText='Longer time frames may take a while to load.  Please exercise caution in queries that extend beyond a months time in this view.'
            >
              <GSOCDateRangePicker value={dateRange} onChange={setDateRange} />
            </FormField>

            <FormField label='Start Region' constraintText='Empty is all.  Example (us-west-2)'>
              <Input onChange={({ detail }) => setStartRegion(detail.value)} value={startRegion} />
            </FormField>
            <FormField
              label='End Region'
              constraintText='Empty is all.  Example (us-west-2), not relevant for DDX since it starts and ends in same region.'
            >
              <Input onChange={({ detail }) => setEndRegion(detail.value)} value={endRegion} />
            </FormField>
            <FormField label='Customer Name Part' constraintText='Use EXTERNAL for all customer contacts'>
              <Input onChange={({ detail }) => setCustomerNamePart(detail.value)} value={customerNamePart} />
            </FormField>

            <br />

            <Header variant='h2'>DDX Metrics</Header>
            <br />
            {ddxData?.listDataPlaneDDXMetric?.results !== undefined ? (
              <DDXGraphs data={ddxData.listDataPlaneDDXMetric.results as DdxMetricsResultFragment[]} />
            ) : (
              <Spinner size='large' />
            )}

            <br />

            <Header variant='h2'>Laminar Flow Metrics</Header>
            <br />
            {lfData?.listDataPlaneLFMetric?.results !== undefined ? (
              <LaminarFlowGraphs data={lfData.listDataPlaneLFMetric.results as LfMetricsResultFragment[]} />
            ) : (
              <Spinner size='large' />
            )}
          </RequiresPermissionClaims>
        </ContentLayout>
      }
      breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.DataPlaneDashboard]}
    />
  );
}
